import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, query, where, or } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Client } from '../types';

const CLIENTS_REF = 'Clients';

const isFirebaseAvailable = () => {
  if (!db) {
    console.error('Firebase not available');
    return false;
  }
  return true;
};

export const getAllClients = async (): Promise<Client[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const clientsRef = collection(db, CLIENTS_REF);
    const querySnapshot = await getDocs(clientsRef);
    const clients: Client[] = [];
    querySnapshot.forEach((doc) => {
      const clientData = doc.data() as Client;
      clients.push({ ...clientData, id: doc.id });
    });
    return clients;
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

export const getClientById = async (clientId: string): Promise<Client | null> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const clientRef = doc(db, CLIENTS_REF, clientId);
    const clientSnap = await getDoc(clientRef);
    if (clientSnap.exists()) {
      return { ...clientSnap.data() as Client, id: clientSnap.id };
    }
    return null;
  } catch (error) {
    console.error('Error fetching client:', error);
    throw error;
  }
};

export const getClientsByStatus = async (status: string): Promise<Client[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const clientsRef = collection(db, CLIENTS_REF);
    const q = query(clientsRef, where('status', '==', status));
    const querySnapshot = await getDocs(q);
    const clients: Client[] = [];
    querySnapshot.forEach((doc) => {
      const clientData = doc.data() as Client;
      clients.push({ ...clientData, id: doc.id });
    });
    return clients;
  } catch (error) {
    console.error('Error fetching clients by status:', error);
    throw error;
  }
};

export const getActiveClients = async (): Promise<Client[]> => {
  return getClientsByStatus('Active');
};

export const addClient = async (clientData: Omit<Client, 'id'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const clientsRef = collection(db, CLIENTS_REF);
    const docRef = await addDoc(clientsRef, clientData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding client:', error);
    throw error;
  }
};

export const updateClient = async (clientId: string, clientData: Partial<Client>): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    const clientRef = doc(db, CLIENTS_REF, clientId);
    await updateDoc(clientRef, clientData);
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

export const deleteClient = async (clientId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    const clientRef = doc(db, CLIENTS_REF, clientId);
    await deleteDoc(clientRef);
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

export const archiveClient = async (clientId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const clientRef = doc(db, CLIENTS_REF, clientId);
    await updateDoc(clientRef, { isArchived: true });
  } catch (error) {
    console.error('Error archiving client:', error);
    throw error;
  }
};

// Get clients by office ID
export const fetchClientsByOfficeID = async (officeId: string): Promise<Client[]> => {
  try {
    console.log('🔍 Debug - fetchClientsByOfficeID called with officeId:', officeId);
    
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const clientsRef = collection(db, CLIENTS_REF);
    const q = query(clientsRef, where('rolesIDs', 'array-contains', officeId));
    console.log('🔍 Debug - Executing Firestore query for clients');
    const querySnapshot = await getDocs(q);
    console.log('🔍 Debug - Firestore query completed, docs count:', querySnapshot.size);
    
    const clients: Client[] = [];
    querySnapshot.forEach((doc) => {
      const clientData = doc.data() as Client;
      console.log('🔍 Debug - Processing client doc:', doc.id, clientData.name);
      clients.push({
        ...clientData,
        id: doc.id
      });
    });
    
    console.log('✅ Debug - fetchClientsByOfficeID completed, returning', clients.length, 'clients');
    return clients;
  } catch (error) {
    console.error('❌ Debug - Error fetching clients by office ID:', error);
    throw error;
  }
};

export const fetchClientsByRoleIDs = async (roleIds: string[]): Promise<Client[]> => {
  try {
    console.log('🔍 Debug - fetchClientsByRoleIDs called with roleIds:', roleIds);
    console.log('🔍 Debug - Role IDs count:', roleIds.length);
    
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    if (roleIds.length === 0) {
      console.log('🔍 Debug - No role IDs provided, returning empty array');
      return [];
    }

    const clientsRef = collection(db, CLIENTS_REF);
    console.log('🔍 Debug - Created clients collection reference');
    
    // Create array-contains queries for each role ID
    const roleQueries = roleIds.map(roleId => 
      where('rolesIDs', 'array-contains', roleId)
    );
    console.log('🔍 Debug - Created role queries:', roleQueries.length, 'queries');
    console.log('🔍 Debug - Role queries details:', roleQueries.map((q, index) => ({
      index,
      roleId: roleIds[index],
      queryType: 'array-contains'
    })));
    
    // Use 'or' to combine all role queries
    const q = query(clientsRef, or(...roleQueries));
    console.log('🔍 Debug - Combined query with OR operator');
    console.log('🔍 Debug - Executing Firestore query for clients with role IDs...');
    
    // Add delay to make the process visible
    console.log('⏳ Debug - Adding 2 second delay to make process visible...');
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const startTime = Date.now();
    const querySnapshot = await getDocs(q);
    const endTime = Date.now();
    
    console.log('🔍 Debug - Firestore query completed');
    console.log('🔍 Debug - Query execution time:', endTime - startTime, 'ms');
    console.log('🔍 Debug - Documents found:', querySnapshot.size);
    
    const clients: Client[] = [];
    let processedCount = 0;
    
    querySnapshot.forEach((doc) => {
      const clientData = doc.data() as Client;
      processedCount++;
      
      console.log(`🔍 Debug - Processing client ${processedCount}/${querySnapshot.size}:`, {
        docId: doc.id,
        clientName: clientData.name,
        clientId: clientData.id,
        rolesIDs: clientData.rolesIDs,
        rolesCount: clientData.rolesIDs?.length || 0,
        mobileNumber: clientData.mobileNumber,
        email: clientData.email,
        createdAt: clientData.createdAt,
        isArchived: clientData.isArchived,
        isDeleted: clientData.isDeleted
      });
      
      clients.push({
        ...clientData,
        id: doc.id
      });
    });
    
    console.log('✅ Debug - fetchClientsByRoleIDs completed successfully');
    console.log('📊 Debug - Final results summary:', {
      totalClients: clients.length,
      roleIdsUsed: roleIds,
      queryTime: endTime - startTime,
      clientsPerRole: roleIds.map(roleId => {
        const clientsWithRole = clients.filter(client => 
          client.rolesIDs?.includes(roleId)
        );
        return { roleId, count: clientsWithRole.length };
      })
    });
    
    return clients;
  } catch (error) {
    console.error('❌ Debug - Error fetching clients by role IDs:', error);
    console.error('❌ Debug - Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      roleIds: roleIds
    });
    throw error;
  }
}; 